import { EventTracker, HoverState, RawAxisProps, SeriesProps, SeriesRef, ValueScale } from '@devexpress/dx-react-chart';
import {
	ArgumentAxis,
	Chart,
	Legend,
	Title,
	ValueAxis,
	Tooltip,
	ZoomAndPan,
	ScatterSeries,
	LineSeries,
} from '@devexpress/dx-react-chart-material-ui';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Colors } from 'shared/assets/root-assets/colors';
import { SkioldTouchableOpacity } from '../../skiold-components/skiold-touchable-opacity';
import { ViewWeb } from '../../utils/web-view';
import { WhiteText } from '../../Text/white-text';

interface Props {
	lineSeries: SeriesProps[];
	title: string;
	chartData: any[];
	valueAxis?: RawAxisProps[];

	keyTranslator?: (key: string) => string | undefined;
	yTitle?: string;
	zoomPan?: 'none' | 'pan' | 'zoom' | 'both';
	verticalArgumentLabels?: boolean;
}
const pointOptions = { size: 5, padding: 0 };
const SplinePoint = props => <ScatterSeries.Point {...props} point={pointOptions} />;

const LinrWithPoints = props => (
	<React.Fragment>
		<LineSeries.Path {...props} />
		<ScatterSeries.Path {...props} />
	</React.Fragment>
);

const makeYLabel = (symbol, color) => (props: ValueAxis.LabelProps) =>
	(
		<ValueAxis.Label
			{...props}
			text={`${props.text} ${symbol}`}
			style={{
				fill: color,
			}}
		/>
	);

const makeXLabel = (symbol, color, vertialLabels) => (props: ValueAxis.LabelProps) =>
	vertialLabels ? (
		<ArgumentAxis.Label
			{...props}
			x={-25 - props.y}
			y={+props.x -8}
			text={`${props.text} ${symbol}`}
			style={{
				fill: color,
				transform: 'rotate(270deg) translateX(-10px)',
			}}
		/>
	) : (
		<ArgumentAxis.Label
			{...props}
			text={`${props.text} ${symbol}`}
			style={{
				fill: color,
			}}
		/>
	);

const TitleText = props => <Title.Text {...props} style={{ color: Colors.white }} />;

export const LineChart: FC<Props> = React.memo(
	({
		lineSeries,
		verticalArgumentLabels,
		zoomPan = 'none',
		yTitle,
		title,
		chartData,
		valueAxis,
		keyTranslator = (key: string) => key,
	}) => {
		// States
		const [target, setTarget] = useState<SeriesRef | undefined>(undefined);
		const [hideLegend, setHideLegend] = useState<string[]>([]);
		const yAxis = useMemo(() => {
			return valueAxis ? (
				valueAxis.map((props: RawAxisProps) => {
					const label = makeYLabel('', Colors.white);
					return <ValueAxis {...props} labelComponent={label} key={props.scaleName} showLine={true} />;
				})
			) : (
				<ValueAxis />
			);
		}, [valueAxis, hideLegend]);

		const yScale = useMemo(() => {
			return valueAxis
				? valueAxis.map((props: RawAxisProps) => {
						return props.scaleName ? <ValueScale name={props.scaleName} /> : null;
				  })
				: null;
		}, [valueAxis]);

		const RootWithTitle = useCallback(
			props => (
				<ViewWeb className="graph-legend-root">
					<div className="rotate-container">
						<WhiteText className="header-white-text rotate-270">{yTitle}</WhiteText>
					</div>

					<Legend.Root {...props} className="graph-legend-root-list" />
				</ViewWeb>
			),
			[],
		);

		// Legend for Hide/Show toggling
		const LegendLabel = useCallback(
			props => (
				<SkioldTouchableOpacity
					onPress={() => {
						// If the legend is already hidden, remove it from the list
						if (hideLegend.includes(props.text)) {
							setHideLegend(hideLegend.filter(x => x !== props.text));
						} else {
							// Otherwise, add it to the list
							setHideLegend([...hideLegend, props.text]);
						}
					}}
				>
					<Legend.Label
						{...props}
						text={keyTranslator(props.text)}
						style={{ color: Colors.white }}
						className={hideLegend.includes(props.text) ? 'line-through' : ''}
					/>
				</SkioldTouchableOpacity>
			),
			[hideLegend],
		);
		// Legend for Hide/Show toggling
		const LegendMarker = useCallback(
			props => (
				<SkioldTouchableOpacity
					onPress={() => {
						// If the legend is already hidden, remove it from the list
						if (hideLegend.includes(props.name)) {
							setHideLegend(hideLegend.filter(x => x !== props.name));
						} else {
							// Otherwise, add it to the list
							setHideLegend([...hideLegend, props.name]);
						}
					}}
				>
					<Legend.Marker {...props} className={hideLegend.includes(props.name) ? 'line-through' : ''} />
				</SkioldTouchableOpacity>
			),
			[hideLegend],
		);

		// Series
		const series = useMemo(() => {
			return lineSeries.map((props: SeriesProps) => {
				const hide = hideLegend.includes(props.valueField);
				if (hide) {
					return <LineSeries {...props} key={props.valueField} color="transparent" />;
				}
				props['pointComponent'] = SplinePoint;
				return <LineSeries {...props} key={props.valueField} seriesComponent={LinrWithPoints} />;
			});
		}, [lineSeries, hideLegend]);

		// Hover
		const onHover = useCallback((target: SeriesRef | null) => {
			setTarget(target ?? undefined);
		}, []);

		// Hide legend values
		const setHideLegendValuesToZero = (singleChartData: any) => {
			const keys = Object.keys(singleChartData);
			// Set values to zero if the legend is hidden
			// We can't just remove BarSeries, legend will then disappear
			keys.forEach(key => {
				if (hideLegend.includes(key)) {
					singleChartData[key] = null;
				}
			});
			return singleChartData;
		};

		// Filter hiden legend data
		const filterHidenLegendData = useMemo(() => {
			return chartData.slice().map(x => setHideLegendValuesToZero({ ...x }));
		}, [chartData, hideLegend]);

		const tooltip = useCallback(
			(target: Tooltip.ContentProps) => {
				const item = lineSeries.find(line => line.valueField === target.targetItem.series);
				if (item) {
					return (
						<tr key={target.targetItem.series + '-' + target.targetItem.point + '-' + item.valueField}>
							<td>
								<svg width="10" height="10">
									<circle cx="5" cy="5" r="5" fill={item.color} />
								</svg>
							</td>
							<td>{keyTranslator(target.targetItem.series)}: </td>
							<td align="right">{target.text}</td>
						</tr>
					);
				}
				return null;
			},
			[lineSeries, hideLegend],
		);

		const overlayComponent = useCallback(
			(props: Tooltip.OverlayProps) => {
				return target?.series && hideLegend.includes(target.series) ? null : <Tooltip.Overlay {...props} />;
			},
			[target, hideLegend],
		);
		return (
			<Chart data={filterHidenLegendData}>
				<ArgumentAxis labelComponent={makeXLabel('', Colors.white, verticalArgumentLabels)} />

				{yScale}
				{yAxis}
				{series}

				<Title textComponent={TitleText} text={title} />
				<Legend labelComponent={LegendLabel} markerComponent={LegendMarker} rootComponent={RootWithTitle} />
				<EventTracker />
				<HoverState hover={target} onHoverChange={onHover} />
				<Tooltip targetItem={target} contentComponent={tooltip} overlayComponent={overlayComponent} />
				{zoomPan !== 'none' && <ZoomAndPan interactionWithArguments={zoomPan} />}
			</Chart>
		);
	},
);
